import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "../../components/layout/AdminLayout";
import { StatusBadge, SkeletonLoader, EmptyState } from "../../components/admin";
import adminService, {
  AdminUserDetails,
} from "../../services/api/adminService";

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<AdminUserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchUserDetails = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await adminService.getUserDetails(id);
      setUser(response.data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch user details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateLong = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-5">
          <div className="h-6 w-40 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2"><SkeletonLoader variant="detail-card" /></div>
            <SkeletonLoader variant="detail-card" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !user) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <p className="text-sm text-red-700 mb-4">{error || "User not found"}</p>
          <Link
            to="/admin/users"
            className="text-sm font-medium text-cyan-600 hover:text-cyan-800 transition-colors"
          >
            &larr; Back to Users
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const InfoField = ({ label, value }: { label: string; value: string | null | undefined }) => (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value || <span className="text-gray-300">--</span>}</p>
    </div>
  );

  const stats = [
    { label: "Applications", value: user.applications.length, color: "text-cyan-600", bg: "bg-cyan-50" },
    { label: "Wishlists", value: user.wishlists.length, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Documents", value: user.documents.length, color: "text-blue-600", bg: "bg-blue-50" },
  ];

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Header */}
        <div>
          <Link
            to="/admin/users"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-cyan-600 transition-colors mb-3"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Users
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                <span className="text-lg font-bold text-white">
                  {user.firstName?.charAt(0) || "U"}{user.lastName?.charAt(0) || ""}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {user.firstName} {user.lastName}
                </h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <StatusBadge status={user.role} type="role" />
                  <StatusBadge
                    status={user.isEmailVerified ? "VERIFIED" : "UNVERIFIED"}
                    type="email"
                  />
                </div>
              </div>
            </div>
            <button
              onClick={fetchUserDetails}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center">
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              <InfoField label="Email" value={user.email} />
              <InfoField label="Role" value={user.role.replace("_", " ")} />
              <InfoField label="Phone" value={user.phone} />
              <InfoField label="Date of Birth" value={user.dateOfBirth ? formatDate(user.dateOfBirth) : null} />
              <InfoField label="Nationality" value={user.nationality} />
              <InfoField label="Country of Residence" value={user.countryOfResidence} />
              <div className="md:col-span-2">
                <InfoField label="Address" value={user.address} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Account
            </h3>
            <div className="space-y-3">
              <InfoField label="Member Since" value={formatDateLong(user.createdAt)} />
              <InfoField label="Last Updated" value={formatDateLong(user.updatedAt)} />
              <div>
                <p className="text-xs text-gray-400 mb-0.5">User ID</p>
                <p className="text-xs text-gray-500 font-mono break-all">{user.id}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Applications Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
              Applications ({user.applications.length})
            </h3>
          </div>
          {user.applications.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Application</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Course</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">University</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {user.applications.map((application) => (
                    <tr key={application.id} className="border-b border-gray-50 hover:bg-cyan-50/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/applications/${application.id}`} className="text-sm font-medium text-cyan-600 hover:text-cyan-800 transition-colors">
                          {application.applicationNumber}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {application.course.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-700">{application.course.university.name}</p>
                        <p className="text-xs text-gray-400">
                          {application.course.university.city}, {application.course.university.country}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={application.status} type="application" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(application.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No applications" description="This user hasn't created any applications yet." />
          )}
        </div>

        {/* Wishlists Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
              Wishlisted Courses ({user.wishlists.length})
            </h3>
          </div>
          {user.wishlists.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Course</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">University</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Added</th>
                  </tr>
                </thead>
                <tbody>
                  {user.wishlists.map((wishlist) => (
                    <tr key={wishlist.id} className="border-b border-gray-50 hover:bg-cyan-50/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {wishlist.course.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {wishlist.course.university.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(wishlist.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No wishlisted courses" description="This user hasn't wishlisted any courses yet." />
          )}
        </div>

        {/* Documents Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
              Documents ({user.documents.length})
            </h3>
          </div>
          {user.documents.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Document</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {user.documents.map((document) => (
                    <tr key={document.id} className="border-b border-gray-50 hover:bg-cyan-50/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {document.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {document.documentType.replace(/_/g, " ")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge
                          status={document.isVerified ? "VERIFIED" : "UNVERIFIED"}
                          type="email"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(document.uploadedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No documents" description="This user hasn't uploaded any documents yet." />
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserDetails;
